import React, { useMemo } from 'react';

import { link } from '@aline/routes';
import { Paper, Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import { compareAsc, formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import { AlineButton } from 'src/components/generic/AlineButton';
import { Spacer } from 'src/components/generic/Spacer';
import { DocumentReportIcon } from 'src/components/icons/DocumentReportIcon';
import { ReminderReportIcon } from 'src/components/icons/ReminderReportIcon';
import { TemplateIcon } from 'src/components/icons/TemplateIcon';
import { TemplateReportIcon } from 'src/components/icons/TemplateReportIcon';
import { myRecentDocumentsKey } from 'src/components/repository';
import { StorableIcon } from 'src/components/StorableIcon';
import { formatIntervalAtHighestDimension } from 'src/components/workflow';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { getIsBrowserHandledLinkClick, openLink } from 'src/helpers';
import { useGetMyMostUsedTemplateWorkflows, useGetReminders } from 'src/hooks';
import { alineLinkButtonStylesWithUnderline } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

const numRows = 4;
const seeMoreId = 'see-more';
const useStyles = makeStyles()(theme => ({
  captionText: {
    whiteSpace: 'nowrap',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 3),
  },
  list: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  paperMargin: {
    marginBottom: theme.spacing(1),
  },
  root: {
    [`&:hover #${seeMoreId}`]: {
      visibility: 'visible',
    },
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(3, 0),
  },
  seeMore: {
    visibility: 'hidden',
  },
}));

export function Templates() {
  const { classes } = useStyles();
  const { loading, templateWorkflows } = useGetMyMostUsedTemplateWorkflows({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const title = 'My Recent Templates';

  if (loading) return <ResourceListSkeleton />;

  if (!templateWorkflows?.length)
    return (
      <ListEmptyState
        action={{
          href: link.TemplateIndex(),
          label: '+ Create a template',
        }}
        icon={<TemplateReportIcon height={80} width={80} />}
        message="Your most-used templates will show up here"
        title={title}
      />
    );

  return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h2">{title}</Typography>
        <AlineButton
          className={classes.seeMore}
          color="primary"
          component={Link}
          id={seeMoreId}
          to={link.TemplateIndex()}
          variant="outlined"
        >
          See More
        </AlineButton>
      </div>
      <div className={classes.list}>
        {templateWorkflows.slice(0, numRows).map(template => (
          <ResourceListRow
            key={template.name}
            icon={<TemplateIcon />}
            linkHref={link.TemplateSelectedTemplateSummary({ workflowId: template.id })}
            name={template.name || 'Untitled'}
          >
            <Typography className={classes.captionText} variant="body2">
              {`Used ${template.numTimesUsed} ${template.numTimesUsed === 1 ? 'time' : 'times'}`}
            </Typography>
          </ResourceListRow>
        ))}
      </div>
    </Paper>
  );
}

export function AiReportsList() {
  const { classes } = useStyles();
  const { data, loading } = Apollo.useGetReportsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const title = 'AI Reports';

  if (loading) return <ResourceListSkeleton />;

  if (!data?.reports.length)
    return (
      <ListEmptyState
        action={{
          href: link.ReportIndex(),
          label: '+ Generate AI report',
        }}
        icon={<DocumentReportIcon height={80} width={80} />}
        message="Reports generated by Aline AI will appear here"
        title={title}
      />
    );

  return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        <Typography component="h2" variant="h2">
          {title}
        </Typography>
        <AlineButton
          className={classes.seeMore}
          color="primary"
          component={Link}
          id={seeMoreId}
          to={link.ReportIndex()}
          variant="outlined"
        >
          See More
        </AlineButton>
      </div>
      {data.reports.slice(0, numRows).map(report => {
        const createdAtString = formatDistanceToNow(new Date(report.createdAt), {
          addSuffix: true,
        });

        return (
          <ResourceListRow
            key={report.id}
            icon={<DocumentReportIcon />}
            linkHref={link.ReportDetail({ reportId: report.id })}
            name={report.name}
          >
            <Typography
              className={classes.captionText}
              variant="body2"
            >{`Created ${createdAtString}`}</Typography>
          </ResourceListRow>
        );
      })}
    </Paper>
  );
}

export function RemindersList() {
  const { classes } = useStyles();
  const currentUserId = useCurrentUserId();
  const { loading, reminders } = useGetReminders({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      filter: Apollo.ReminderFilter.Incomplete,
    },
  });
  const myIncompleteReminders = useMemo(
    () =>
      reminders
        .filter(reminder => reminder.assignees.some(assignee => assignee.id === currentUserId))
        .sort((a, b) => {
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;

          return compareAsc(new Date(a.dueDate), new Date(b.dueDate));
        }),
    [reminders, currentUserId]
  );
  const title = 'My Reminders';

  if (loading) return <ResourceListSkeleton />;

  if (!myIncompleteReminders.length)
    return (
      <ListEmptyState
        action={{
          href: link.RemindersIndex(),
          label: '+ Add a reminder',
        }}
        className={classes.paperMargin}
        icon={<ReminderReportIcon height={80} width={80} />}
        message="All your reminders will be shown here"
        title={title}
      />
    );

  return (
    <Paper className={clsx(classes.root, classes.paperMargin)}>
      <div className={classes.header}>
        <Typography component="h2" variant="h2">
          {title}
        </Typography>
        <AlineButton
          className={classes.seeMore}
          color="primary"
          component={Link}
          id={seeMoreId}
          to={link.RemindersIndex()}
          variant="outlined"
        >
          See More
        </AlineButton>
      </div>
      {myIncompleteReminders.slice(0, numRows).map(reminder => {
        const dueDateString = reminder.dueDate
          ? formatDistanceToNow(new Date(reminder.dueDate), {
              addSuffix: true,
            })
          : 'date not set';

        return (
          <ResourceListRow
            key={reminder.id}
            icon={<ReminderReportIcon />}
            linkHref={link.RemindersDetail({ reminderId: reminder.id })}
            name={reminder.title || 'Untitled'}
          >
            <Typography
              className={classes.captionText}
              variant="body2"
            >{`Due ${dueDateString}`}</Typography>
          </ResourceListRow>
        );
      })}
    </Paper>
  );
}

export function RecentDocuments() {
  const { classes } = useStyles();
  const { data, loading } = Apollo.useGetMyRecentDocumentsQuery({
    fetchPolicy: 'cache-first',
  });
  const [, setExpanded] = useLocalStorage<boolean>(myRecentDocumentsKey, true);
  const title = 'My Recent Documents';

  if (loading) return <ResourceListSkeleton />;

  if (!data?.currentUser?.myRecentDocuments.length)
    return (
      <ListEmptyState
        action={{
          href: link.RepositoryDocuments(),
          label: '+ Upload a document',
        }}
        icon={<DocumentReportIcon height={80} width={80} />}
        message="When documents are ready, they will appear here"
        title={title}
      />
    );

  return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        <Typography component="h2" variant="h2">
          {title}
        </Typography>
        <AlineButton
          className={classes.seeMore}
          color="primary"
          component={Link}
          id={seeMoreId}
          to={link.RepositoryDocuments()}
          variant="outlined"
          onClick={() => {
            setExpanded(true);
          }}
        >
          See More
        </AlineButton>
      </div>
      {data.currentUser.myRecentDocuments.slice(0, numRows).map(document => {
        const viewedAtString = formatIntervalAtHighestDimension({
          end: new Date(),
          start: new Date(document.lastAccessedByRequesterAt ?? document.createdAt),
        });

        return (
          <ResourceListRow
            key={document.id}
            icon={<StorableIcon storable={document} />}
            linkHref={link.DocumentDetail({ documentId: document.id })}
            name={document.name}
            onNavigate={href => {
              openLink(href, { title: document.name || 'Untitled Document' });
            }}
          >
            <Typography className={classes.captionText} variant="body2">
              {`Viewed ${viewedAtString} ago`}
            </Typography>
          </ResourceListRow>
        );
      })}
    </Paper>
  );
}

const useResourceListRowStyles = makeStyles()(theme => ({
  root: {
    '&:hover': {
      backgroundColor: theme.appColors.background,
    },
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 3),
    textDecoration: 'none',
  },
}));

function ResourceListRow({
  children,
  icon,
  linkHref,
  name,
  onNavigate,
}: {
  icon: React.ReactNode;
  linkHref: string;
  name: string;
  children?: React.ReactNode;
  onNavigate?: (href: string) => void;
}) {
  const { classes } = useResourceListRowStyles();

  return (
    <Link
      className={classes.root}
      to={linkHref}
      onClick={e => {
        e.stopPropagation();
        /**
         * Keep the anchor's href meaningful, but let callers take over a plain left-click
         * (e.g. opening the document in an Aline tab instead of a route change). Modifier
         * clicks stay with the browser so "open in new tab" still works.
         */
        if (!onNavigate || getIsBrowserHandledLinkClick(e)) return;
        e.preventDefault();
        onNavigate(linkHref);
      }}
    >
      {icon}
      <Typography>{name}</Typography>
      <Spacer />
      {children}
    </Link>
  );
}

function ResourceListSkeleton() {
  const { classes: rootStyles } = useStyles();
  const { classes } = useResourceListRowStyles();

  return (
    <Paper className={rootStyles.root}>
      <div className={rootStyles.header}>
        <Typography variant="h2">
          <Skeleton variant="text" width={100} />
        </Typography>
      </div>
      <div className={rootStyles.list}>
        {Array.from({ length: numRows }).map((_, index) => (
          <div key={index} className={classes.root}>
            <Skeleton height={24} variant="rectangular" width={24} />
            <Typography>
              <Skeleton height={24} variant="text" width={150} />
            </Typography>
          </div>
        ))}
      </div>
    </Paper>
  );
}

const useListEmptyStateStyles = makeStyles()(theme => ({
  icon: {
    alignItems: 'center',
    backgroundColor: '#F5F6FB',
    borderRadius: '50%',
    display: 'flex',
    height: 80,
    justifyContent: 'center',
    width: 80,
  },
  linkText: {
    ...alineLinkButtonStylesWithUnderline,
  },
  messageContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    justifyContent: 'center',
    padding: theme.spacing(4, 2),
  },
}));

export function ListEmptyState({
  action,
  className,
  icon,
  message,
  title,
}: {
  action: { href: string; label: string };
  icon: React.ReactNode;
  message: string;
  title: string;
  className?: string;
}) {
  const { classes } = useListEmptyStateStyles();

  return (
    <Paper className={clsx(classes.root, className)}>
      <div className={classes.icon}>{icon}</div>
      <div className={classes.messageContainer}>
        <Typography variant="subtitle1">{title}</Typography>
        <Typography>{message}</Typography>
        <Typography className={classes.linkText} component={Link} to={action.href}>
          {action.label}
        </Typography>
      </div>
    </Paper>
  );
}
